import _ from "lodash";
import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";

import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import withLoader from "UIComponents/GqlWithLoader";
import { useI18n } from "Hooks";
import { Popover } from "@toddle-edu/react-tiny-popover";
import { getIsAdminByRole } from "lib/staffPermissionUtils";

import {
  getCourseUsersQuery,
  getGradeUsersQuery,
  getOrganizationCourseUsersQuery,
} from "MessagingV2/modules/MessagingQuery";
import {
  getCourseUsersFromCache,
  getGradeUsersFromCache,
  getOrganizationCourseUsersFromCache,
} from "MessagingV2/modules/MessagingGraphqlHelper";
import { getOrganizationId } from "modules/CommonSelectors";

import { getUserListEnhancer } from "MessagingV2/modules/MessagingEnhancers";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import {
  CHAT_STATUS_ENUM,
  getChatsAndChannelEnabledCurriculum,
  isCreateChatsAndChannelsEnableForAll,
  isMessagingRnpEnabled,
  RNP_CAN_CREATE_AND_MANAGE_CHATS_AND_CHANNELS_FOR_CLASS,
} from "MessagingV2/modules/Utils";
import { prepareLoadMoreFeed } from "Utils";

import UserTypeList from "MessagingV2/components/ThreadList/CreateNewChatModal/components/UserTypeList";
import { withChatOptions } from "Platform/routes/MessagingV2/providers/ChatOptionsProvider";
import CohortsList from "../CohortsList";
import classes from "./UsersAndCohortsPopover.scss";

const getUserIdAndType = user => ({
  id: _.get(user, "node.id"),
  type: _.get(user, "node.type"),
});
const getCoursesCohorts = _.memoize(courses => {
  const coursesCohorts = [];
  _.forEach(courses, course => {
    const {
      students = [],
      collaborator = [],
      familyMembers = [],
      id,
      title,
    } = _.get(course, "node", {});
    const studentsList = _.map(_.get(students, "edges", []), getUserIdAndType);
    const collaboratorList = _.map(
      _.get(collaborator, "edges", []),
      getUserIdAndType
    );

    const familyMembersList = _.map(
      _.get(familyMembers, "edges", []),
      getUserIdAndType
    );

    if (!_.isEmpty(studentsList)) {
      coursesCohorts.push({
        id,
        title,
        type: STUDENT,
        userList: studentsList,
      });
    }
    if (!_.isEmpty(collaboratorList)) {
      coursesCohorts.push({
        id,
        title,
        type: STAFF,
        userList: collaboratorList,
      });
    }
    if (!_.isEmpty(familyMembersList)) {
      coursesCohorts.push({
        id,
        title,
        type: PARENT,
        userList: familyMembersList,
      });
    }
  });
  return coursesCohorts;
});

const getGradesCohorts = _.memoize(grades => {
  const gradeUsersCohorts = [];
  _.forEach(grades, grade => {
    const { id, name, staffCount, studentCount, familyMemberCount } = grade;

    if (studentCount) {
      gradeUsersCohorts.push({
        id,
        title: name,
        type: STUDENT,
        count: studentCount,
      });
    }
    if (staffCount) {
      gradeUsersCohorts.push({
        id,
        title: name,
        type: STAFF,
        count: staffCount,
      });
    }
    if (familyMemberCount) {
      gradeUsersCohorts.push({
        id,
        title: name,
        type: PARENT,
        count: familyMemberCount,
      });
    }
  });
  return gradeUsersCohorts;
});

const UsersAndCohortsPopoverOverlay = props => {
  const {
    searchText,
    userParams = {},
    handleUsersAdd,
    handleGradeAdd,
    userTypeListContainerClassName,
    courseUserParams,
    gradeUserParams,
  } = props;
  const { t } = useI18n(["common"]);

  const hideUsersList = _.isEmpty(userParams["userNodeList"]);
  const hideCoursesCohortsList = _.isEmpty(courseUserParams["cohorts"]);
  const hideGradeCohortsList = _.isEmpty(gradeUserParams["cohorts"]);

  const isEmptyResult =
    hideUsersList && hideCoursesCohortsList && hideGradeCohortsList;

  // todo remove this temp changes for grade pagination

  const [paginationCount, setPaginationCount] = useState(3);

  const gradeCohortsCount = _.size(gradeUserParams["cohorts"]);

  const loadMoreGradeUsers = useCallback(() => {
    setPaginationCount(prev => prev + 3);
  }, []);

  const gradeTempParams = {
    cohorts: _.slice(gradeUserParams["cohorts"], 0, paginationCount),
    hasNextPage: gradeCohortsCount > paginationCount,
    isFetchingMore: false,
    loadMoreUsers: loadMoreGradeUsers,
  };

  if (isEmptyResult) {
    return (
      <div className={classes.emptyStateContainer}>
        {t("common:no_user_found_for_label", {
          label: searchText,
        })}
      </div>
    );
  }
  const onUserClick = user => handleUsersAdd([user]);

  return (
    <>
      {!hideGradeCohortsList ? (
        <CohortsList
          headerTitle={t("common:grades")}
          onClick={handleGradeAdd}
          isGrade
          userTypeListContainerClassName={userTypeListContainerClassName}
          {...gradeUserParams}
          {...gradeTempParams}
        />
      ) : null}
      {!hideGradeCohortsList && !hideCoursesCohortsList ? (
        <div className={classes.divider} />
      ) : null}
      {!hideCoursesCohortsList ? (
        <CohortsList
          headerTitle={t("common:class_plural")}
          onClick={handleUsersAdd}
          userTypeListContainerClassName={userTypeListContainerClassName}
          {...courseUserParams}
        />
      ) : null}
      {!hideUsersList && (!hideGradeCohortsList || !hideCoursesCohortsList) ? (
        <div className={classes.divider} />
      ) : null}
      {!hideUsersList ? (
        <UserTypeList
          headerTitle={t("common:member_other")}
          onUserClick={onUserClick}
          userTypeListContainerClassName={userTypeListContainerClassName}
          {...userParams}
        />
      ) : null}
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userId = _.get(ownProps, "activeUserId");
  return {
    isData: true,
    isLoading: false,
    userId: userId,
    organizationId: getOrganizationId(state),
  };
};

const getVariables = () => ({
  userStatus: "ACTIVE",
  chatStatus: CHAT_STATUS_ENUM.ALL,
  includeInitiatedChatUsers: false,
});

const UsersAndCohortsPopoverOverlayWithLoader = compose(
  withChatOptions(),
  connect(mapStateToProps),
  getUserListEnhancer({
    userType: [STAFF, STUDENT, PARENT],
    getVariables,
  }),
  graphql(getOrganizationCourseUsersQuery, {
    name: "getOrganizationCourseUsers",
    skip: () => isMessagingRnpEnabled() || !getIsAdminByRole(),
    options: props => {
      const { organizationId, searchText } = props;
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId,
          first: 3,
          portalType: "PLANNER",
          filters: {
            searchText,
          },
        },
      };
    },
    props: ({ getOrganizationCourseUsers, ownProps }) => {
      const { isLoading, isData } = ownProps;

      const queryVariables = _.get(getOrganizationCourseUsers, "variables", {});
      const cachedData = getOrganizationCourseUsersFromCache(
        getOrganizationCourseUsers.variables
      );

      const courses = _.get(cachedData, "courses.edges", []);
      const hasNextPage = _.get(
        cachedData,
        "courses.pageInfo.hasNextPage",
        false
      );

      const coursesCohorts = getCoursesCohorts(courses);

      const courseUserParams = {
        cohorts: coursesCohorts,
        hasNextPage,
        isFetchingMore: _.includes(
          [3],
          getOrganizationCourseUsers["networkStatus"]
        ),
        loadMoreUsers: prepareLoadMoreFeed({
          query: getOrganizationCourseUsersQuery,
          variables: {
            ...queryVariables,
            after: _.get(cachedData, "courses.pageInfo.endCursor"),
            first: 10,
          },
          queryData: getOrganizationCourseUsers,
          key: "courses",
        }),
      };
      return {
        isData: !_.isEmpty(cachedData) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationCourseUsers["networkStatus"]) ||
          isLoading,
        courseUserParams,
      };
    },
  }),
  graphql(getCourseUsersQuery, {
    name: "getCourseUsers",
    skip: () => !isMessagingRnpEnabled() && getIsAdminByRole(),
    options: props => {
      const { userId, searchText } = props;
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: userId,
          first: 3,
          portalType: "PLANNER",
          filters: {
            searchText,
            ...(isMessagingRnpEnabled()
              ? {
                  fetchOnlyMappedCourses: false,
                  controlParameters: [
                    RNP_CAN_CREATE_AND_MANAGE_CHATS_AND_CHANNELS_FOR_CLASS,
                  ],
                }
              : {}),
          },
        },
      };
    },
    props: ({ getCourseUsers, ownProps }) => {
      const { isLoading, isData } = ownProps;

      const queryVariables = _.get(getCourseUsers, "variables", {});
      const cachedData = getCourseUsersFromCache(getCourseUsers.variables);

      const courses = _.get(cachedData, "coursesV2.edges", []);
      const hasNextPage = _.get(
        cachedData,
        "coursesV2.pageInfo.hasNextPage",
        false
      );

      const coursesCohorts = getCoursesCohorts(courses);

      const courseUserParams = {
        cohorts: coursesCohorts,
        hasNextPage,
        isFetchingMore: _.includes([3], getCourseUsers["networkStatus"]),
        loadMoreUsers: prepareLoadMoreFeed({
          query: getCourseUsersQuery,
          variables: {
            ...queryVariables,
            after: _.get(cachedData, "coursesV2.pageInfo.endCursor"),
            first: 10,
          },
          queryData: getCourseUsers,
          key: "coursesV2",
        }),
      };
      return {
        isData: !_.isEmpty(cachedData) && isData,
        isLoading:
          _.includes([1, 2], getCourseUsers["networkStatus"]) || isLoading,
        courseUserParams,
      };
    },
  }),
  graphql(getGradeUsersQuery, {
    name: "getGradeUsers",
    skip: () =>
      isMessagingRnpEnabled() && !isCreateChatsAndChannelsEnableForAll(),
    options: props => {
      const { organizationId, searchText } = props;
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId,
          first: 3,
          portalType: "PLANNER",
          filters: {
            searchText,
            ...(isMessagingRnpEnabled()
              ? {
                  curriculumProgramIds: getChatsAndChannelEnabledCurriculum(),
                }
              : {}),
          },
        },
      };
    },
    props: ({ getGradeUsers, ownProps }) => {
      const { isLoading, isData } = ownProps;

      const queryVariables = _.get(getGradeUsers, "variables", {});
      const cachedData = getGradeUsersFromCache(getGradeUsers.variables);

      const grades = _.get(cachedData, "grades", []);
      const hasNextPage = _.get(
        cachedData,
        "grades.pageInfo.hasNextPage",
        false
      );

      const gradesCohorts = getGradesCohorts(grades);
      const gradeUserParams = {
        cohorts: gradesCohorts,
        hasNextPage,
        isFetchingMore: _.includes([3], getGradeUsers["networkStatus"]),
        loadMoreUsers: prepareLoadMoreFeed({
          query: getGradeUsersQuery,
          variables: {
            ...queryVariables,
            after: _.get(cachedData, "grades.pageInfo.endCursor"),
            first: 10,
          },
          queryData: getGradeUsers,
          key: "courses",
        }),
      };
      return {
        isData: !_.isEmpty(cachedData) && isData,
        isLoading:
          _.includes([1, 2], getGradeUsers["networkStatus"]) || isLoading,
        gradeUserParams,
      };
    },
  }),

  withLoader
)(UsersAndCohortsPopoverOverlay);

const UsersAndCohortsPopover = props => {
  const {
    containerRef,
    searchText,
    handleUsersAdd,
    handleGradeAdd,
    excludedUserIds,
    overlayContainerStyle,
    isOpen,
    includePaidUsersOnly = false,
  } = props;

  return (
    <Popover
      isOpen={isOpen}
      positions={["bottom"]}
      reposition={false}
      containerClassName={classes.popoverContainer}
      containerParent={containerRef?.current}
      content={
        <div className={classes.overlayContainer} style={overlayContainerStyle}>
          <UsersAndCohortsPopoverOverlayWithLoader
            searchText={searchText}
            handleUsersAdd={handleUsersAdd}
            handleGradeAdd={handleGradeAdd}
            style={overlayContainerStyle}
            includePaidUsersOnly={includePaidUsersOnly}
            excludedUserIds={excludedUserIds}
          />
        </div>
      }
    >
      <div />
    </Popover>
  );
};

UsersAndCohortsPopover.propTypes = {
  containerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    .isRequired,
  searchText: PropTypes.string.isRequired,
  handleUsersAdd: PropTypes.func.isRequired,
  handleGradeAdd: PropTypes.func.isRequired,
  overlayContainerStyle: PropTypes.object,
};

export default UsersAndCohortsPopover;
